import { EntityLinks, Goal, GoalProgressCount, ID } from '@griply/types';

import { getArchivedGoalIds } from './derived-goal-state';
import { buildGoalTree, GoalTreeNode } from './goals/build-goal-tree';
import { sortGoals } from './sort-goals';

// Completed goals stay selectable — their tasks must remain findable via the goal filter.
export type BuildGoalFilterOptionTreeOptions = {
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  links: EntityLinks;
};

export const buildGoalFilterOptionTree = (
  goals: Goal[],
  { taskCounts, goalProgressCounts, links }: BuildGoalFilterOptionTreeOptions,
): GoalTreeNode[] => {
  const archivedIds = getArchivedGoalIds(goals, { links });
  const candidates = goals.filter(
    (goal) => !archivedIds.has(goal.id) && !goal.frozenAt,
  );

  const sortChildren = (
    nodes: GoalTreeNode[],
    parent: Goal,
  ): GoalTreeNode[] => {
    const order = new Map(
      sortGoals(
        nodes.map(({ goal }) => goal),
        {
          allGoals: candidates,
          sortMode: parent.goalSortingMode,
          customOrder: parent.goalSorting,
          taskCounts,
          goalProgressCounts,
          links,
        },
      ).map((goal, index) => [goal.id, index]),
    );

    return nodes
      .slice()
      .sort((a, b) => (order.get(a.goal.id) ?? 0) - (order.get(b.goal.id) ?? 0))
      .map((node) => ({
        goal: node.goal,
        children: sortChildren(node.children, node.goal),
      }));
  };

  return buildGoalTree(candidates, { allGoals: goals, links }).map((node) => ({
    goal: node.goal,
    children: sortChildren(node.children, node.goal),
  }));
};
